/**
 * Teaching Team API Utilities
 * Provides functions for interacting with the Teaching Team API endpoints
 */

// Get CSRF token from meta tag
const getCSRFToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
};

// Common headers for API requests
const getHeaders = () => ({
  'Accept': 'application/json',
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCSRFToken()
});

// Headers for FormData requests (no Content-Type — browser sets boundary)
const getFormDataHeaders = () => ({
  'Accept': 'application/json',
  'X-CSRF-Token': getCSRFToken()
});

// Helper to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    // Session expired (CSRF token stale after idle on mobile)
    if (response.status === 401) {
      window.location.reload();
      throw new Error('Session expired');
    }
    // A 500 page or a proxy error is not JSON; then the server wrote no message.
    const error = await response.json().catch(() => ({}));
    const serverMessage = error.error || error.errors?.join(', ');
    const err = new Error(serverMessage || 'Request failed');
    // A machine-readable reason, when the server gives one, so callers do not parse messages.
    err.code = error.code;
    err.fromServer = Boolean(serverMessage);
    throw err;
  }
  return response.json();
};

/**
 * The line to show the user for a failed request: the server's own message
 * when it wrote one, otherwise the caller's translated fallback.
 */
export const userFacingError = (err, fallback) => (err?.fromServer ? err.message : fallback);

// ===============================
// Teaching Teams
// ===============================

/**
 * Get all teaching teams for current user
 */
export const getTeachingTeams = async () => {
  const response = await fetch('/api/teaching_teams', {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Get a single teaching team
 */
export const getTeachingTeam = async (teamId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Get team dashboard data
 */
export const getTeamDashboard = async (teamId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/dashboard`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Get team visit stats (owner/admin only)
 */
export const getTeamVisitStats = async (teamId) => {
  const response = await fetch(`/dashboard/teams/${teamId}/stats/visits`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Create a new teaching team
 */
export const createTeachingTeam = async (data) => {
  const response = await fetch('/api/teaching_teams', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ teaching_team: data })
  });
  return handleResponse(response);
};

/**
 * Update a teaching team
 */
export const updateTeachingTeam = async (teamId, data) => {
  const response = await fetch(`/api/teaching_teams/${teamId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ teaching_team: data })
  });
  return handleResponse(response);
};

/**
 * Update team booking mode
 */
export const updateBookingMode = async (teamId, bookingMode) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/booking_mode`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ booking_mode: bookingMode })
  });
  return handleResponse(response);
};

/**
 * Delete a teaching team
 */
export const deleteTeachingTeam = async (teamId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Delete failed' }));
    throw new Error(error.error || 'Delete failed');
  }
  return true;
};

/**
 * Regenerate team invite code
 */
export const regenerateInviteCode = async (teamId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/regenerate_invite_code`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(response);
};

// ===============================
// Team Memberships
// ===============================

/**
 * Get team memberships
 */
export const getTeamMemberships = async (teamId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/memberships`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Invite a member to team
 */
export const inviteMember = async (teamId, data) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/memberships`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ membership: data })
  });
  return handleResponse(response);
};

/**
 * Update a team membership
 */
export const updateMembership = async (teamId, membershipId, data) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/memberships/${membershipId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ membership: data })
  });
  return handleResponse(response);
};

/**
 * Remove a team membership
 */
export const removeMembership = async (teamId, membershipId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/memberships/${membershipId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Remove failed' }));
    throw new Error(error.error || 'Remove failed');
  }
  // 200 with JSON body means there's a warning (e.g., unsettled payslips)
  if (response.status === 200) {
    const data = await response.json().catch(() => ({}));
    return data;
  }
  return true;
};

/**
 * Get a member's recurring weekly schedule
 */
export const getMemberSchedule = async (teamId, membershipId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/memberships/${membershipId}/schedule`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

// ===============================
// Team Invitations
// ===============================

/**
 * Get pending invitations for current user
 */
export const getPendingInvitations = async () => {
  const response = await fetch('/api/team_invitations/pending', {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Accept an invitation
 */
export const acceptInvitation = async (invitationId) => {
  const response = await fetch(`/api/team_invitations/${invitationId}/accept`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Reject an invitation
 */
export const rejectInvitation = async (invitationId) => {
  const response = await fetch(`/api/team_invitations/${invitationId}/reject`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(response);
};

// ===============================
// Team Applications
// ===============================

/**
 * Get applications for a team
 */
export const getTeamApplications = async (teamId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/applications`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Create an application to join a team
 */
export const createApplication = async (teamId, message, formResponses = null) => {
  const applicationData = { message }
  if (formResponses) {
    applicationData.form_responses = formResponses
  }
  const response = await fetch(`/api/teaching_teams/${teamId}/applications`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ application: applicationData })
  });
  return handleResponse(response);
};

/**
 * Accept an application
 */
export const acceptApplication = async (applicationId, revenueShareType, revenueShareValue, revenueShareValuePlatform) => {
  const body = {
    revenue_share_type: revenueShareType,
    revenue_share_value: revenueShareValue
  }
  if (revenueShareValuePlatform != null) {
    body.revenue_share_value_platform = revenueShareValuePlatform
  }
  const response = await fetch(`/api/team_applications/${applicationId}/accept`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body)
  });
  return handleResponse(response);
};

/**
 * Reject an application
 */
export const rejectApplication = async (applicationId, reason) => {
  const response = await fetch(`/api/team_applications/${applicationId}/reject`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ reason })
  });
  return handleResponse(response);
};

// ===============================
// Team Courses
// ===============================

/**
 * Get courses for a team
 */
export const getTeamCourses = async (teamId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/courses`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Get a single course
 */
export const getTeamCourse = async (teamId, courseId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/courses/${courseId}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Create a course
 */
export const createTeamCourse = async (teamId, data) => {
  const isFormData = data instanceof FormData;
  const response = await fetch(`/api/teaching_teams/${teamId}/courses`, {
    method: 'POST',
    headers: isFormData
      ? getFormDataHeaders()
      : getHeaders(),
    body: isFormData ? data : JSON.stringify({ course: data })
  });
  return handleResponse(response);
};

/**
 * Update a course
 */
export const updateTeamCourse = async (teamId, courseId, data) => {
  const isFormData = data instanceof FormData;
  const response = await fetch(`/api/teaching_teams/${teamId}/courses/${courseId}`, {
    method: 'PATCH',
    headers: isFormData
      ? getFormDataHeaders()
      : getHeaders(),
    body: isFormData ? data : JSON.stringify({ course: data })
  });
  return handleResponse(response);
};

/**
 * Delete a course
 */
export const deleteTeamCourse = async (teamId, courseId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/courses/${courseId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Delete failed' }));
    throw new Error(error.error || 'Delete failed');
  }
  return true;
};

/**
 * Get available slots for a course
 */
export const getCourseAvailableSlots = async (teamId, courseId, startDate, endDate, timezone) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (timezone) params.append('timezone', timezone);

  const response = await fetch(`/api/teaching_teams/${teamId}/courses/${courseId}/available_slots?${params}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

// ===============================
// Course Packages
// ===============================

/**
 * Get packages for a course
 */
export const getCoursePackages = async (courseId) => {
  const response = await fetch(`/api/team_courses/${courseId}/packages`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Create a package
 */
export const createCoursePackage = async (courseId, data) => {
  const response = await fetch(`/api/team_courses/${courseId}/packages`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ package: data })
  });
  return handleResponse(response);
};

/**
 * Update a package
 */
export const updateCoursePackage = async (courseId, packageId, data) => {
  const response = await fetch(`/api/team_courses/${courseId}/packages/${packageId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ package: data })
  });
  return handleResponse(response);
};

/**
 * Delete a package
 */
export const deleteCoursePackage = async (courseId, packageId) => {
  const response = await fetch(`/api/team_courses/${courseId}/packages/${packageId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Delete failed' }));
    throw new Error(error.error || 'Delete failed');
  }
  return true;
};

// ===============================
// Team Bookings
// ===============================

/**
 * Get overdue bookings (confirmed but past end_time)
 */
export const getOverdueBookings = async (teamId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/overdue_bookings`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Get bookings for a team
 */
export const getTeamBookings = async (teamId, filters = {}) => {
  const params = new URLSearchParams();
  const bookingId = filters.bookingId || filters.booking_id;
  const status = filters.status;
  const teacherId = filters.teacherId || filters.teacher_id;
  const startDate = filters.startDate || filters.start_date;
  const endDate = filters.endDate || filters.end_date;
  const page = filters.page;
  const perPage = filters.perPage || filters.per_page;

  if (bookingId) params.append('booking_id', bookingId);
  if (status) params.append('status', status);
  if (teacherId) params.append('teacher_id', teacherId);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (page) params.append('page', page);
  if (perPage) params.append('per_page', perPage);

  const response = await fetch(`/api/teaching_teams/${teamId}/bookings?${params}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Create a booking
 */
export const createTeamBooking = async (courseId, data) => {
  const response = await fetch(`/api/team_courses/${courseId}/bookings`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ booking: data })
  });
  return handleResponse(response);
};

/**
 * Get a booking
 */
export const getBooking = async (bookingId) => {
  const response = await fetch(`/api/bookings/${bookingId}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Complete a booking (mark lesson as done)
 */
export const completeBooking = async (bookingId, reason) => {
  const body = reason ? JSON.stringify({ reason }) : undefined;
  const response = await fetch(`/api/bookings/${bookingId}/complete`, {
    method: 'POST',
    headers: getHeaders(),
    ...(body && { body })
  });
  return handleResponse(response);
};

/**
 * Cancel a booking
 */
export const cancelBooking = async (bookingId, reason) => {
  const response = await fetch(`/api/bookings/${bookingId}/cancel`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ reason })
  });
  return handleResponse(response);
};

/**
 * Update a booking (reschedule date/time, change teacher)
 */
export const updateBooking = async (bookingId, data) => {
  const response = await fetch(`/api/bookings/${bookingId}/reschedule`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

/**
 * Late reschedule request: propose a new time for a lesson inside its notice
 * window. proposedStartTime is a naive "YYYY-MM-DDTHH:MM" read in timezone — the
 * zone the requester typed it in.
 */
export const createRescheduleRequest = async (bookingId, proposedStartTime, timezone) => {
  const response = await fetch(`/api/bookings/${bookingId}/reschedule_requests`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ proposed_start_time: proposedStartTime, timezone })
  });
  return handleResponse(response);
};

/**
 * Answer or withdraw a late reschedule request. action: approve | decline | withdraw
 */
export const respondToRescheduleRequest = async (requestId, action) => {
  const response = await fetch(`/api/reschedule_requests/${requestId}/${action}`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Get pending students for a team
 */
export const getPendingStudents = async (teamId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/pending_students`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

// ===============================
// Team Students
// ===============================

/**
 * Get students for a team
 */
export const getTeamStudents = async (teamId, filters = {}) => {
  const params = new URLSearchParams();
  if (filters.source) params.append('source', filters.source);
  if (filters.page) params.append('page', filters.page);
  if (filters.perPage) params.append('per_page', filters.perPage);

  const response = await fetch(`/api/teaching_teams/${teamId}/students?${params}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Get a single team student detail (includes form submission data)
 */
export const getTeamStudent = async (teamId, studentId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/students/${studentId}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Update a team student's timezone
 */
export const updateStudentTimezone = async (teamId, studentId, timezone) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/students/${studentId}/timezone`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ timezone })
  });
  return handleResponse(response);
};

// ===============================
// Payslips
// ===============================

/**
 * Get my payslips
 */
export const getMyPayslips = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.page) params.append('page', filters.page);
  if (filters.perPage) params.append('per_page', filters.perPage);

  const response = await fetch(`/api/my_payslips?${params}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Get a single payslip
 */
export const getMyPayslip = async (payslipId) => {
  const response = await fetch(`/api/my_payslips/${payslipId}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Get team payslips (for owner/admin)
 */
export const getTeamPayslips = async (teamId, filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.memberId) params.append('member_id', filters.memberId);
  if (filters.billingMonth) params.append('billing_month', filters.billingMonth);
  if (filters.page) params.append('page', filters.page);
  if (filters.perPage) params.append('per_page', filters.perPage);

  const response = await fetch(`/api/teaching_teams/${teamId}/payslips?${params}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Get a team payslip
 */
export const getTeamPayslip = async (teamId, payslipId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/payslips/${payslipId}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Mark a payslip as paid
 */
export const markPayslipPaid = async (teamId, payslipId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/payslips/${payslipId}/mark_paid`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Generate payslips for a billing month
 */
export const generatePayslips = async (teamId, billingMonth) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/payslips/generate`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ billing_month: billingMonth })
  });
  return handleResponse(response);
};

// ===============================
// Student Course Balances
// ===============================

/**
 * Get my course balances
 */
export const getMyCourseBalances = async () => {
  const response = await fetch('/api/my_balances', {
    headers: getHeaders()
  });
  return handleResponse(response);
};

// ===============================
// Payslip Preview
// ===============================

/**
 * Get payslip preview (projected earnings from unsettled bookings)
 */
export const getPayslipPreview = async (teamId, billingMonth) => {
  const params = new URLSearchParams();
  if (billingMonth) params.append('billing_month', billingMonth);

  const response = await fetch(`/api/teaching_teams/${teamId}/payslips/preview?${params}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Get settled lesson details for a payslip (from settled bookings)
 */
export const getSettledLessonDetails = async (teamId, billingMonth) => {
  const params = new URLSearchParams();
  params.append('team_id', teamId);
  if (billingMonth) params.append('billing_month', billingMonth);

  const response = await fetch(`/api/my_payslips/lesson_details?${params}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

// ===============================
// Team Forms (Intake & Recruitment)
// ===============================

/**
 * Get the team's form by type
 */
export const getTeamForm = async (teamId, formType = 'intake') => {
  const response = await fetch(`/api/teaching_teams/${teamId}/team_form?form_type=${formType}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Create a team form from template
 */
export const createTeamForm = async (teamId, templateKey, formType = 'intake') => {
  const response = await fetch(`/api/teaching_teams/${teamId}/team_form`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ template_key: templateKey, form_type: formType })
  });
  return handleResponse(response);
};

/**
 * Update team form (fields, is_active)
 */
export const updateTeamForm = async (teamId, data, formType = 'intake') => {
  const response = await fetch(`/api/teaching_teams/${teamId}/team_form?form_type=${formType}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ team_form: data })
  });
  return handleResponse(response);
};

/**
 * Delete team form
 */
export const deleteTeamForm = async (teamId, formType = 'intake') => {
  const response = await fetch(`/api/teaching_teams/${teamId}/team_form?form_type=${formType}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Delete failed' }));
    throw new Error(error.error || 'Delete failed');
  }
  return true;
};

/**
 * Reset team form to default template fields
 */
export const resetTeamForm = async (teamId, formType = 'intake') => {
  const response = await fetch(`/api/teaching_teams/${teamId}/team_form/reset?form_type=${formType}`, {
    method: 'PATCH',
    headers: getHeaders()
  });
  return handleResponse(response);
};

/**
 * Submit intake form (student-facing)
 */
export const submitIntakeForm = async (teamId, responses) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/form_submissions`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ responses })
  });
  return handleResponse(response);
};

// ===============================
// Team Contact
// ===============================

/**
 * Contact team (create conversation with owner, optionally submit recruitment form)
 */
export const contactTeam = async (teamId, formResponses = null) => {
  const body = formResponses ? { form_responses: formResponses } : {};
  const response = await fetch(`/api/teaching_teams/${teamId}/contact`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body)
  });
  return handleResponse(response);
};

// ===============================
// Student Consultation
// ===============================

export const submitConsultation = async (teamId, formResponses, consultationMessage, { startTime, source, email, name, timezone, memberId } = {}) => {
  const body = {
    form_responses: formResponses,
    consultation_message: consultationMessage
  };
  if (startTime) body.start_time = startTime;
  if (source) body.source = source;
  if (email) body.email = email;
  if (name) body.name = name;
  if (timezone) body.timezone = timezone;
  if (memberId) body.member_id = memberId;
  const response = await fetch(`/api/teaching_teams/${teamId}/consultation`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body)
  });
  return handleResponse(response);
};

// ===============================
// Teacher Recruitment
// ===============================

/**
 * Get recruiterable teachers for a team
 */
export const getRecruitTeachers = async (teamId, { subject, page } = {}) => {
  const params = new URLSearchParams();
  if (subject) params.set('subject', subject);
  if (page) params.set('page', page);
  const query = params.toString();
  const url = `/api/teaching_teams/${teamId}/recruit${query ? `?${query}` : ''}`;
  const response = await fetch(url, { headers: getHeaders() });
  return handleResponse(response);
};

/**
 * Send a team invitation to a teacher
 */
export const createTeamInvitation = async (teamId, { invitee_id, message }) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/invitations`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ invitee_id, message })
  });
  return handleResponse(response);
};

// Course Orders
// ===============================

/**
 * Create a course order
 */
export const createCourseOrder = async (data) => {
  const response = await fetch('/api/course_orders', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ order: data })
  });
  return handleResponse(response);
};

// ===============================
// Student Pins (Map)
// ===============================

export const getStudentPins = async (teamId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/student_pins`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const createStudentPin = async (teamId, formData) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/student_pins`, {
    method: 'POST',
    headers: getFormDataHeaders(),
    body: formData
  });
  return handleResponse(response);
};

export const updateStudentPin = async (teamId, pinId, formData) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/student_pins/${pinId}`, {
    method: 'PATCH',
    headers: getFormDataHeaders(),
    body: formData
  });
  return handleResponse(response);
};

export const deleteStudentPin = async (teamId, pinId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/student_pins/${pinId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }
  return true;
};

// ===============================
// Community Posts
// ===============================

export const recordCommunityPostView = async (teamId, postId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/community_posts/${postId}/views`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const getCommunityPosts = async (teamId, { categoryId, after, limit } = {}) => {
  const params = new URLSearchParams();
  if (categoryId) params.set('category_id', categoryId);
  if (after) params.set('after', after);
  if (limit) params.set('limit', limit);
  const query = params.toString() ? `?${params}` : '';
  const response = await fetch(`/api/teaching_teams/${teamId}/community_posts${query}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const createCommunityPost = async (teamId, { title, body, communityategoryId, images, notifyStudents }) => {
  const formData = new FormData();
  if (title) formData.append('title', title);
  formData.append('body', body);
  if (communityategoryId) formData.append('community_category_id', communityategoryId);
  if (notifyStudents) formData.append('notify_students', 'true');
  if (images) {
    images.forEach(img => formData.append('images[]', img));
  }
  const response = await fetch(`/api/teaching_teams/${teamId}/community_posts`, {
    method: 'POST',
    headers: getFormDataHeaders(),
    body: formData
  });
  return handleResponse(response);
};

export const updateCommunityPost = async (teamId, postId, { title, body, communityategoryId, images, removeImageIds }) => {
  const formData = new FormData();
  if (title !== undefined) formData.append('title', title);
  if (body !== undefined) formData.append('body', body);
  if (communityategoryId !== undefined) formData.append('community_category_id', communityategoryId || '');
  if (images) {
    images.forEach(img => formData.append('images[]', img));
  }
  if (removeImageIds) {
    removeImageIds.forEach(id => formData.append('remove_image_ids[]', id));
  }
  const response = await fetch(`/api/teaching_teams/${teamId}/community_posts/${postId}`, {
    method: 'PATCH',
    headers: getFormDataHeaders(),
    body: formData
  });
  return handleResponse(response);
};

export const deleteCommunityPost = async (teamId, postId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/community_posts/${postId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const pinCommunityPost = async (teamId, postId, pinned) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/community_posts/${postId}/pin`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ pinned })
  });
  return handleResponse(response);
};

// ===============================
// Community Comments
// ===============================

export const getCommunityComments = async (teamId, postId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/community_posts/${postId}/comments`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const createCommunityComment = async (teamId, postId, body) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/community_posts/${postId}/comments`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ body })
  });
  return handleResponse(response);
};

export const deleteCommunityComment = async (commentId) => {
  const response = await fetch(`/api/community_comments/${commentId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return handleResponse(response);
};

// ===============================
// Community Likes
// ===============================

export const toggleCommunityLike = async (teamId, postId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/community_posts/${postId}/like`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(response);
};

// ===============================
// Community Membership
// ===============================

export const joinCommunity = async (teamId, { email } = {}) => {
  const options = {
    method: 'POST',
    headers: getHeaders()
  };
  if (email) {
    options.body = JSON.stringify({ email });
  }
  const response = await fetch(`/api/teaching_teams/${teamId}/join_community`, options);
  return handleResponse(response);
};

// ===============================
// Community Categories
// ===============================

export const getCommunityCategories = async (teamId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/community_categories`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const createCommunityCategory = async (teamId, name) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/community_categories`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ name })
  });
  return handleResponse(response);
};

export const updateCommunityCategory = async (teamId, categoryId, name) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/community_categories/${categoryId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ name })
  });
  return handleResponse(response);
};

export const deleteCommunityCategory = async (teamId, categoryId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/community_categories/${categoryId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const reorderCommunityCategories = async (teamId, categoryIds) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/community_categories/reorder`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ category_ids: categoryIds })
  });
  return handleResponse(response);
};

// Newsletters
export const sendNewsletter = async (teamId, { subject, body }) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/newsletters`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ subject, body })
  });
  return handleResponse(response);
};

export const getNewsletters = async (teamId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/newsletters`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

// Student Invitations
export const getStudentInvitations = async (teamId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/student_invitations`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const createStudentInvitation = async (teamId, email) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/student_invitations`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ email })
  });
  return handleResponse(response);
};

export const resendStudentInvitation = async (teamId, invitationId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/student_invitations/${invitationId}/resend`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const cancelStudentInvitation = async (teamId, invitationId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/student_invitations/${invitationId}/cancel`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const acceptStudentInvitation = async (token) => {
  const response = await fetch(`/api/student_invitations/${token}/accept`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(response);
};

// ===============================
// Auth (Email/Password)
// ===============================

export const checkEmail = async (email) => {
  const response = await fetch('/api/auth/check_email', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ email })
  });
  return handleResponse(response);
};

export const registerUser = async (email, name, password) => {
  const response = await fetch('/api/auth/register', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ email, name, password })
  });
  return handleResponse(response);
};

// ===============================
// Promotion Templates
// ===============================

export const getPromotionTemplates = async (teamId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/promotion_templates`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

export const createPromotionTemplate = async (teamId, data) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/promotion_templates`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ promotion_template: data })
  });
  return handleResponse(response);
};

export const updatePromotionTemplate = async (teamId, templateId, data) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/promotion_templates/${templateId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ promotion_template: data })
  });
  return handleResponse(response);
};

export const deletePromotionTemplate = async (teamId, templateId) => {
  const response = await fetch(`/api/teaching_teams/${teamId}/promotion_templates/${templateId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Delete failed' }));
    throw new Error(error.error || 'Delete failed');
  }
  return true;
};

export const copyPromotionTemplate = async (teamId, templateId, lastLink) => {
  const body = lastLink ? JSON.stringify({ last_link: lastLink }) : undefined
  const response = await fetch(`/api/teaching_teams/${teamId}/promotion_templates/${templateId}/copy`, {
    method: 'POST',
    headers: getHeaders(),
    body
  });
  return handleResponse(response);
};

export const getPromotionStats = async (teamId, period = '30d') => {
  const response = await fetch(`/api/teaching_teams/${teamId}/promotion_stats?period=${period}`, {
    headers: getHeaders()
  });
  return handleResponse(response);
};

// Custom response handling — can't use handleResponse because it reloads on 401 (session expired),
// but login 401 means wrong password, not expired session.
export const loginUser = async (email, password) => {
  const response = await fetch('/users/sign_in.json', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ user: { email, password } })
  });
  const data = await response.json().catch(() => ({ success: false, errors: ['Request failed'] }));
  if (!response.ok || !data.success) {
    throw new Error(data.errors?.join(', ') || 'Login failed');
  }
  return data;
};
